import { apiGet, apiPost } from '@/shared/api/http';
import { EP } from '@/shared/api/wire';
import type { Body, Res } from '@/shared/api/wire';

/**
 * 通知行（读面出参）—— **行的元信息 + 业务定位信息**。
 *
 * ⚠️ 行里**没有 `payload`**：那是落行当刻的快照（可能已过期）⇒ 读面只给
 * 「哪件事、什么时候、什么档位、哪个实体」，前端据此 refetch 业务数据，
 * 而不是拿快照当现值（与「帧 = 提示、不是数据」同口径）。
 */
export type Notice = Res<typeof EP.notificationsList, 'get'>['items'][number];

/** 通知行分页—— `items` + `page` / `pages` / `size` / `total`。 */
export type NoticesPage = Res<typeof EP.notificationsList, 'get'>;

/** 未读数——铃铛角标。 */
export type UnreadCount = Res<typeof EP.notificationsUnreadCount, 'get'>;

/**
 * 一页的条数。
 *
 * ⚠️ 分页按**页**走，不把 `size` 一直加大：后端的 `size` 有上限
 * （`MAX_PAGE_SIZE`，200），一直加大迟早撞 ceiling 报 422。
 */
export const NOTICES_PAGE_SIZE = 20;

export interface NoticesQuery {
  page: number;
  size: number;
  /** 只要未读 ⇒ 交给后端过滤（`unread_only`），**不在前端筛**。 */
  unreadOnly: boolean;
}

export function fetchNotices(query: NoticesQuery): Promise<NoticesPage> {
  return apiGet(EP.notificationsList, {
    query: {
      page: query.page,
      size: query.size,
      // 省略即后端默认（「默认全部」）⇒ 只在要未读时才把这个键发出去。
      unread_only: query.unreadOnly ? true : undefined,
    },
  });
}

export function fetchUnreadCount(): Promise<UnreadCount> {
  return apiGet(EP.notificationsUnreadCount);
}

/** 标已读——**只能标自己的**（别人的行后端回 404）。 */
export function markNoticeRead(notificationId: string): Promise<Notice> {
  return apiPost(EP.notificationsMarkRead, { params: { notification_id: notificationId } });
}

// -- 公告（admin 发）-------------------------------------------------------------

/** 公告请求体——`recipient_user_ids` **不给或给空 ⇒ 全员广播**。 */
export type BroadcastInput = NonNullable<Body<typeof EP.notificationsBroadcast, 'post'>>;

/** 打断强度——两档大写（与后端 DB CHECK 逐字相等）。 */
export type NotificationImportance = BroadcastInput['importance'];

/** 公告回执——`scope` 是 `ALL`（全员）/ `TARGETED`（定向），`recipients` 是收件人数。 */
export type BroadcastOut = Res<typeof EP.notificationsBroadcast, 'post'>;

export interface BroadcastNotice {
  message: string;
  importance: NotificationImportance;
}

/**
 * 发公告——**admin 的平台级动作**（后端 `AdminDep` 守卫，非 admin 直接 403）。
 *
 * ⚠️ **不给 `recipient_user_ids` 就是全员广播**：后端按「剔空去重后还剩几个」判口径
 * （`notifications/service.py::broadcast_scope`）⇒ 空名单与不给同义。
 */
export function broadcastNotice(input: BroadcastNotice): Promise<BroadcastOut> {
  return apiPost(EP.notificationsBroadcast, {
    body: {
      message: input.message,
      importance: input.importance,
    },
  });
}
